import { useState } from "react";
import { ErrorState } from "../shell/ui";
import { hub, instanceStatus, localRuntimeApplied, wakesOnDemand, type AgentInstance } from "./api";
import "../connections/settings-list.css";

export type AgentRunAction = "start" | "stop";

interface AgentRunProps {
  agent: AgentInstance;
  onUpdated?: (agent: AgentInstance) => void;
  busy: AgentRunAction | null;
  onBusyChange: (action: AgentRunAction | null) => void;
  error?: unknown;
  onError: (error: unknown) => void;
}

/**
 * The current Agent's status and lifecycle action, beside its settings identity.
 *
 * Stopping is the only dangerous press here, so it asks once, in place, and says what it costs. The Hub always
 * draws this in the same place, whether the Agent's own settings are loading, connected, sleeping or stopped.
 * The Hub holds the one copy of the Agent it shows everywhere else.
 */
export function AgentRun({ agent, onUpdated, busy, onBusyChange, error, onError }: AgentRunProps) {
  const [asking, setAsking] = useState(false);
  const status = instanceStatus(agent);
  const local = agent.location === "local" && Boolean(agent.client_id);
  const offline = local && agent.client?.online === false;
  const stopping = local && agent.desired_running === false && !localRuntimeApplied(agent) && !offline;
  const needsStop = busy ? busy === "stop" : local
    ? agent.desired_running === true || (agent.desired_running === false && localRuntimeApplied(agent) && agent.local_runtime?.state === "error")
    : wakesOnDemand(agent) || agent.online || ["running", "starting", "restarting", "stopping"].includes(agent.runner?.state || "")
      || (agent.desired_running === false && Boolean(agent.runner?.last_error));
  const change = async () => {
    if (busy) return;
    const action = needsStop ? "stop" : "start";
    onBusyChange(action); onError(null); setAsking(false);
    try {
      const result = await hub[action](agent.id);
      onUpdated?.(result.agent);
    } catch (failure) { onError(failure); }
    finally { onBusyChange(null); }
  };
  const label = busy ? local ? "正在保存请求…" : busy === "stop" ? "正在停止，等待实例确认…" : "正在启动…" : status.label;
  const tone = busy ? "warning" : status.tone;
  return <section className={`settings-block agent-run${asking && needsStop ? " is-confirming" : ""}`} aria-label="Agent 运行">
    <div>
      <p className="settings-block-title" role="status"><span className={`settings-dot is-${tone}`} aria-hidden="true" />{label}</p>
      {agent.location === "cloud" && !agent.runner ? <p>云端运行服务暂不可用，请联系管理员。</p> : null}
      {local && offline && !localRuntimeApplied(agent) ? <p className="agent-run-note">打开客户端后自动继续。</p> : null}
      {localRuntimeApplied(agent) && agent.local_runtime?.error ? <p data-quoted="true">{agent.local_runtime.error}</p> : null}
      {agent.runner?.last_error ? <p data-quoted="true">{agent.runner.last_error}</p> : null}
    </div>
    {local || (agent.location === "cloud" && agent.runner) ? <div className="settings-actions">
      {needsStop ? asking ? <>
        <button className="btn btn-critical" type="button" disabled={busy !== null} onClick={() => { void change(); }}>
          {busy === "stop" ? local ? "正在保存…" : "正在停止…" : offline ? "上线后停止" : "确认停止"}</button>
        <button className="btn btn-quiet" type="button" disabled={busy !== null} onClick={() => setAsking(false)}>取消</button>
      </> : <button className="btn btn-quiet agent-run-control" type="button" disabled={busy !== null}
        onClick={() => setAsking(true)}>{busy === "stop" ? local ? "正在保存…" : "正在停止…" : offline ? "上线后停止" : "停止运行"}</button>
        : <button className="btn btn-accent" type="button" disabled={busy !== null || stopping}
          onClick={() => { void change(); }}>{busy === "start" ? local ? "正在保存…" : "正在启动…" : stopping ? "正在停止…" : offline ? "上线后启动" : "启动"}</button>}
    </div> : null}
    {asking && needsStop ? <p className="settings-block-wide">停止后，正在做的事会中断，也收不到新消息。之后随时可以重新启动。</p> : null}
    {error ? <div className="settings-block-wide"><ErrorState error={error} /></div> : null}
  </section>;
}
